import * as React from 'react';
import {
    CreateButton,
    DateField,
    FilterButton,
    List,
    NumberField,
    SearchInput,
    TopToolbar,
    TextField,
    DatagridConfigurable,
    SelectColumnsButton,
    useTranslate,
    FunctionField,
    useNotify,
    WithListContext,
    DatagridLoading,
    Link,
    downloadCSV,
    ExportButton,
    useListContext,
    Pagination,
} from 'react-admin';
import { useMediaQuery, Theme, Typography } from '@mui/material';
import { Alert } from '@mui/material';

import CustomStatusField from '../../controls/CustomStatusField';
import PupilListAside from './PupilListAside';
import get from 'lodash/get';
import CustomPagination from '../../common/CustomPagination';
import SourceNameField from '../SourceNameField';
import SessionBadge from '../controls/SessionBadge';
import ConditionalLoginField from '../ConditionalLoginField';
import SchoolNameField from '../SchoolNameField';
// import ProductFullNameField from '../product/ProductFullNameField';
// import SubscriberFullNameField from './SubscriberFullNameField';
import jsonExport from 'jsonexport/dist';
import CreateUserDialogButton from '../CreateUserDialogButton';
import { IfCanAccess } from '@react-admin/ra-rbac';
import { useCanAccess } from '@react-admin/ra-rbac';
const pupilFilters = [<SearchInput source="q" alwaysOn />];

const ListActions = () => {
    const translate = useTranslate();
    const { filterValues } = useListContext();
    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
    return (
        <TopToolbar>
            {isSmall ? <FilterButton /> : null}

            {/* <CreateButton /> */}
            {filterValues?.parent_group && filterValues?.status == 10 && (
                <ExportButton
                    label={translate(
                        `resources.pupil.actions.export.${filterValues?.status}`
                    )}
                    disabled={
                        !filterValues?.parent_group ||
                        filterValues?.status != 10
                    }
                    maxResults={1000}
                    meta={{
                        filter: {
                            need_count: false,
                            sessions: false,
                            licenses: true,
                        },
                    }}
                />
            )}
            <IfCanAccess action="create" resource="pupil">
                <CreateUserDialogButton resource="pupil" />
            </IfCanAccess>
            <SelectColumnsButton />
            {/* <ListViewButton /> */}
        </TopToolbar>
    );
};

const PupilList = () => {
    const notify = useNotify();
    const { canAccess: canWritePupil } = useCanAccess({
        action: 'write',
        resource: 'pupil',
    });
    const handleOnClick = e => {
        e.stopPropagation();
        navigator.clipboard.writeText(e.target.innerText);
        //notify(e.target.innerText + ' kopioitu leikepöydälle.', { type: 'info' });
        notify(
            <Alert severity="info">
                {e.target.innerText} kopioitu leikepöydälle.
            </Alert>
        );
    };

    const isXsmall = useMediaQuery<Theme>(theme =>
        theme.breakpoints.down('sm')
    );
    const sortable = [
        'public.firstname',
        'public.lastname',
        'last_login',
        'registered',
        'services',
    ];

    const translate = useTranslate();

    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));

    const exporter = pupils => {
        const usersForExport = pupils.map(pupil => {
            const userForExport = {
                id: pupil.id,
                Etunimi: pupil.public.firstname,
                Sukunimi: pupil.public.lastname,
                Avainkoodi: pupil.public.code,
                'Kirjauduttu viimeksi': get(pupil, 'last_login', ''),
                Rekisteröity: pupil.registered,
                Oppilaitokset: pupil.schools
                    .map(school => school.name)
                    .join(', '),
                Mpass: pupil.services?.includes('mpass') ? 'Kyllä' : 'Ei',
                Lisenssit: pupil.licenses
                    .filter(
                        license =>
                            license.status === 0 &&
                            Date.parse(license.valid_until) > Date.now()
                    )
                    .map(license => license.name)
                    .join(', '),
            };
            return userForExport;
        });
        jsonExport(
            usersForExport,
            {
                headers: [
                    'id',
                    'Etunimi',
                    'Sukunimi',
                    'Avainkoodi',
                    'Mpass',
                    'Oppilaitokset',
                    'Kirjauduttu viimeksi',
                    'Rekisteröity',
                    'Lisenssit',
                ],
                rowDelimiter: ';',
            },
            (err, csv) => {
                if (err) {
                    console.error('Export error:', err);
                    notify(translate('notifications.EXPORT_ERROR'), {
                        type: 'error',
                    });
                    return;
                }
                downloadCSV(csv, 'users'); // download as 'users.csv' file
            }
        );
    };

    return (
        <List
            filters={isSmall ? pupilFilters : undefined}
            sort={{ field: 'registered', order: 'DESC' }}
            filter={{ need_count: true, sessions: true, licenses: false }}
            perPage={50}
            pagination={
                <Pagination
                    rowsPerPageOptions={[
                        10,
                        25,
                        50,
                        100,
                        200,
                        { label: 'Kaikki (max 500)', value: 500 },
                    ]}
                />
            }
            aside={<PupilListAside />}
            actions={<ListActions />}
            //pagination={false ? <CustomPagination /> : undefined}
            exporter={exporter}
        >
            <WithListContext
                render={({ isLoading, isFetching, data }) =>
                    isLoading || isFetching ? (
                        <DatagridLoading nbChildren={6} nbFakeLines={8} />
                    ) : (
                        <DatagridConfigurable
                            /*
                            rowSx={record => ({
                                color:
                                    record.status === 0
                                        ? 'none'
                                        : 'rgba(255, 0, 0, 0.05)',
                            })}
                            */
                            //optimized
                            omit={['guid']}
                            rowClick="edit"
                            //rowClick={(id: any, basePath: any, record: any) => {window.open("https://oppimateriaalit-aspa.otava.fi/#/"+basePath+"/"+id, "_blank");return false;}}
                            bulkActionButtons={
                                canWritePupil ? undefined : false
                            }
                            sx={{
                                '& .column-groups': {
                                    md: { display: 'none' },
                                    lg: { display: 'table-cell' },
                                },
                            }}
                        >
                            {/* {columns} */}
                            <TextField
                                source="guid"
                                key="guid"
                                sortable={sortable.includes('guid')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.firstname"
                                key="firstname"
                                sortable={sortable.includes('public.firstname')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.lastname"
                                key="lastname"
                                sortable={sortable.includes('public.lastname')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.code"
                                key="code"
                                sortable={sortable.includes('public.code')}
                                onClick={handleOnClick}
                            />
                            <SchoolNameField
                                source="school.title"
                                key="school"
                                sortable={sortable.includes('school')}
                            />
                            <SessionBadge
                                source="last_login"
                                options={{
                                    showTTL: false,
                                }}
                                sortByOrder="DESC"
                            >
                                <ConditionalLoginField
                                    showTime
                                    locales="fi-FI"
                                    sortable={sortable?.includes('last_login')} // Conditional sortable
                                    sortByOrder="DESC"
                                />
                            </SessionBadge>
                            <DateField
                                source="registered"
                                key="registered"
                                showTime={false}
                                locales="fi-FI"
                                sortable={sortable.includes('registered')}
                                sortByOrder="DESC"
                            />
                            <SourceNameField label="Lähde" size="small" />
                        </DatagridConfigurable>
                    )
                }
            />
        </List>
    );
};

export default PupilList;
